import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { PropertyLimitChoiceDialog } from "@/components/PropertyLimitChoiceDialog";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, MapPin, LogIn, LogOut, Loader2, Camera, Check, Clock, History,
} from "lucide-react";

const ROOM_SUGGESTIONS = ["Living Room", "Kitchen", "Bedroom", "Bathroom", "Hallway", "Balcony"];

export default function Start() {
  const navigate = useNavigate();
  const { openUpgrade } = useTier();
  const [sp] = useSearchParams();
  const qAddr = sp.get("address") || "";
  const qDoc = sp.get("doc") === "move_out" || sp.get("doc") === "move_in" ? sp.get("doc") : null;
  const qStage = qDoc === "move_out" ? "moving_out" : (qDoc === "move_in" ? "moving_in" : null);
  const [stage, setStage] = useState(qStage);
  const [address, setAddress] = useState(qAddr);
  const [docType, setDocType] = useState(qDoc);
  const [importMode, setImportMode] = useState(qStage && qStage !== "moving_in" ? "live" : null);
  const [firstRoom, setFirstRoom] = useState("Living Room");
  const [busy, setBusy] = useState(false);
  const [limitChoice, setLimitChoice] = useState(false);
  const [activeProps, setActiveProps] = useState([]);
  // Step order: 0 address → 1 tenancy stage → 2 import mode (moving-in only) → 3 first room.
  const [step, setStep] = useState(qAddr && qStage ? (qStage === "moving_in" ? 2 : 3) : 0);

  const STAGE_TO_DOC = { moving_in: "move_in", mid_lease: "move_in", moving_out: "move_out" };
  const pickStage = (s) => {
    setStage(s);
    setDocType(STAGE_TO_DOC[s]);
    setImportMode(s === "moving_in" ? null : "live");
  };

  // Free users are limited to one active property. If they already have one,
  // don't drop them into the creation wizard (which would 402) — offer a choice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [propsRes, statusRes] = await Promise.all([
          api.get("/properties"),
          api.get("/billing/status"),
        ]);
        if (cancelled) return;
        // Only genuinely ACTIVE (non-archived, non-sample/demo) properties occupy the single
        // free slot — archived properties are read-only and must not block a new property.
        const real = (propsRes.data || []).filter(
          (p) => !p.is_demo && !p.is_sample && p.archive_status !== "archived"
        );
        if (statusRes.data?.tier !== "premium" && real.length >= 1) {
          setActiveProps(real.map((p) => ({ property_id: p.property_id, address: p.address })));
          setLimitChoice(true);
        }
      } catch { /* on failure, allow the wizard; backend still enforces the limit */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const next = () => setStep((s) => (s === 1 && stage !== "moving_in" ? 3 : s + 1));
  const back = () => {
    if (step === 0) return navigate("/dashboard");
    setStep((s) => (s === 3 && stage !== "moving_in" ? 1 : s - 1));
  };

  const finish = async () => {
    setBusy(true);
    try {
      const { data: prop } = await api.post("/properties", { address, doc_type: docType, import_mode: importMode || "live", documented_stage: stage });
      const { data: room } = await api.post(`/properties/${prop.property_id}/rooms`, { name: firstRoom });
      const msg = stage === "mid_lease" ? "Late baseline started — documenting today beats never."
        : stage === "moving_out" ? "Move-out documentation started — capture the final walkthrough."
        : "Property created — let's start shooting";
      toast.success(msg);
      navigate(`/capture/${prop.property_id}/${room.room_id}`);
    } catch (e) {
      toast.error("Could not create property");
      setBusy(false);
    }
  };

  const canContinue = step === 0 ? address.trim().length > 3
    : step === 1 ? !!stage
    : step === 2 ? !!importMode
    : firstRoom.trim().length > 0;
  const onContinue = () => (step === 3 ? finish() : next());

  return (
    <div className="min-h-screen bg-[#EEF2F6] flex flex-col">
      <header className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button data-testid="onboarding-back-btn" onClick={back} className="flex items-center gap-2 text-[#475569] hover:text-navy transition-colors">
            <ArrowLeft size={18} /> Back
          </button>
          <Logo />
          <div className="w-16" />
        </div>
        <div className="h-1 bg-[#F1F5F9]">
          <motion.div className="h-full bg-brand" animate={{ width: `${((step + 1) / 4) * 100}%` }} transition={{ duration: 0.3 }} />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-10">
        <div className="w-full max-w-lg">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <Panel key="addr">
                <StepHeader label="Step 1 of 4" title="Where's the rental?" sub="Enter the property address you're documenting." />
                <div className="relative">
                  <MapPin size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                  <Input
                    data-testid="address-input"
                    autoFocus
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && canContinue && onContinue()}
                    placeholder="123 Main St, Apt 4, City"
                    className="h-14 pl-12 rounded-2xl text-base bg-white border-[#E2E8F0]"
                  />
                </div>
                <p className="mt-3 text-sm text-[#94A3B8]">You can fill in deposit amount, dates and landlord details later.</p>
              </Panel>
            )}

            {step === 1 && (
              <Panel key="stage">
                <StepHeader label="Step 2 of 4" title="Where are you in this rental?" sub="We'll set up the right kind of documentation for your situation." />
                <div className="grid grid-cols-1 gap-4">
                  <TypeCard
                    testId="stage-moving-in"
                    active={stage === "moving_in"} onClick={() => pickStage("moving_in")}
                    icon={LogIn} title="Just moving in" desc="Record the starting condition as you arrive."
                  />
                  <TypeCard
                    testId="stage-mid-lease"
                    active={stage === "mid_lease"} onClick={() => pickStage("mid_lease")}
                    icon={History} title="Living here already" desc="Document today's condition as a late baseline. Documenting today still beats documenting never."
                  />
                  <TypeCard
                    testId="stage-moving-out"
                    active={stage === "moving_out"} onClick={() => pickStage("moving_out")}
                    icon={LogOut} title="Moving out soon" desc="Capture a final walkthrough and prepare your deposit return."
                  />
                </div>
              </Panel>
            )}

            {step === 2 && (
              <Panel key="mode">
                <StepHeader label="Step 3 of 4" title="When did you move in?" sub="We'll set up documentation the right way for your situation." />
                <div className="grid grid-cols-1 gap-4">
                  <TypeCard
                    testId="importmode-live"
                    active={importMode === "live"} onClick={() => setImportMode("live")}
                    icon={Clock} title="Just now / recently" desc="I'll take photos with my camera as I go through each room."
                  />
                  <TypeCard
                    testId="importmode-retroactive"
                    active={importMode === "retroactive"} onClick={() => setImportMode("retroactive")}
                    icon={History} title="A while ago — I have older photos" desc="I'll upload existing photos. Their original dates are read from the file metadata and shown on your report."
                  />
                </div>
              </Panel>
            )}

            {step === 3 && (
              <Panel key="room">
                <StepHeader label="Step 4 of 4" title="First room to photograph" sub="Pick where you'll start. Add more rooms anytime." />
                <div className="flex flex-wrap gap-2.5">
                  {ROOM_SUGGESTIONS.map((r) => (
                    <button
                      key={r}
                      data-testid={`room-chip-${r.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => setFirstRoom(r)}
                      className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all ${
                        firstRoom === r
                          ? "bg-navy text-white border-navy"
                          : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#CBD5E1]"
                      }`}
                    >
                      {firstRoom === r && <Check size={14} className="inline mr-1.5 -mt-0.5" />}
                      {r}
                    </button>
                  ))}
                </div>
                <div className="mt-5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Or type a custom room</label>
                  <Input
                    data-testid="custom-room-input"
                    value={firstRoom}
                    onChange={(e) => setFirstRoom(e.target.value)}
                    placeholder="e.g. Master Bathroom"
                    className="mt-1.5 h-12 rounded-xl bg-white"
                  />
                </div>
              </Panel>
            )}
          </AnimatePresence>

          <Button
            data-testid="onboarding-continue-btn"
            onClick={onContinue}
            disabled={!canContinue || busy}
            className="mt-8 w-full h-14 rounded-2xl bg-brand hover:bg-brand-hover text-white text-base font-medium shadow-[0_8px_24px_rgba(15,27,45,0.3)] disabled:opacity-50"
          >
            {busy ? <Loader2 className="animate-spin" size={20} /> : step === 3 ? (
              <>Start photographing <Camera size={18} className="ml-2" /></>
            ) : (
              <>Continue <ArrowRight size={18} className="ml-2" /></>
            )}
          </Button>

          {step === 3 && (
            <button
              data-testid="quick-start-cta"
              onClick={() => navigate("/quick-start")}
              className="mt-4 w-full text-center text-sm font-medium text-brand hover:underline"
            >
              In a hurry? Seal one room in 5 minutes →
            </button>
          )}
        </div>
      </div>

      <PropertyLimitChoiceDialog
        open={limitChoice}
        onOpenChange={(o) => { if (!o) navigate("/dashboard"); }}
        activeProperties={activeProps}
        onView={(id) => { setLimitChoice(false); if (id) navigate(`/property/${id}`); }}
        onUpgrade={() => { setLimitChoice(false); navigate("/dashboard"); openUpgrade("multiple_properties"); }}
        onArchive={(id) => { setLimitChoice(false); if (id) navigate(`/property/${id}?archive=1`); }}
        onManageAll={() => { setLimitChoice(false); navigate("/dashboard"); }}
      />
    </div>
  );
}

function Panel({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.28 }}
    >
      {children}
    </motion.div>
  );
}

function StepHeader({ label, title, sub }) {
  return (
    <div className="mb-6">
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-brand">{label}</p>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-navy">{title}</h1>
      <p className="mt-2 text-[#475569]">{sub}</p>
    </div>
  );
}

function TypeCard({ active, onClick, icon: Icon, title, desc, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`text-left rounded-2xl border-2 p-5 transition-all ${
        active ? "border-amber bg-amber-soft" : "border-[#E2E8F0] bg-white hover:border-[#CBD5E1]"
      }`}
    >
      <span className={`grid place-items-center h-11 w-11 rounded-xl ${active ? "bg-brand text-white" : "bg-[#F1F5F9] text-[#475569]"}`}>
        <Icon size={22} />
      </span>
      <h3 className="mt-3 font-heading text-lg font-semibold text-navy">{title}</h3>
      <p className="mt-1 text-sm text-[#475569] leading-relaxed">{desc}</p>
    </button>
  );
}
